import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Loader2,
  Mic2,
  Music2,
  Play,
  Pause,
  CheckCircle2,
  Trash2,
  Upload,
  Sparkles,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import logoAsset from "@/assets/logo.jpg.asset.json";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListRadio,
  adminGenerateEpisode,
  adminSetEpisodeStatus,
  adminDeleteEpisode,
  adminCreateTrack,
  adminToggleTrack,
  adminDeleteTrack,
} from "@/lib/api/radio.functions";
import { RadioTextToVideo } from "@/components/admin/RadioTextToVideo";

type WatermarkProgress = { phase: "loading" | "encoding" | "done"; ratio: number };
type PublishStage = "idle" | "uploading-thumb" | "uploading-video" | "recording-track" | "done";

const BRAND_LINE = "© Nkadimeng Danny Legal & Compliance Support";
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;


export const Route = createFileRoute("/_authenticated/portal/admin/radio")({
  head: () => ({ meta: [{ title: "Admin · Radio" }] }),
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-red-600">Error: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-6">Not found</div>,
  component: RadioAdmin,
});

function RadioAdmin() {
  const qc = useQueryClient();
  const list = useServerFn(adminListRadio);
  const gen = useServerFn(adminGenerateEpisode);
  const setStatus = useServerFn(adminSetEpisodeStatus);
  const delEp = useServerFn(adminDeleteEpisode);
  const createTrack = useServerFn(adminCreateTrack);
  const toggleTrack = useServerFn(adminToggleTrack);
  const delTrack = useServerFn(adminDeleteTrack);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-radio"],
    queryFn: () => list(),
  });

  const [topic, setTopic] = useState("");
  const [title, setTitle] = useState("");
  const [trackTitle, setTrackTitle] = useState("");
  const [trackArtist, setTrackArtist] = useState("");
  const [uploading, setUploading] = useState(false);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoArtist, setVideoArtist] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoProgress, setVideoProgress] = useState<WatermarkProgress | null>(null);
  const [videoPreview, setVideoPreview] = useState<
    | {
        url: string;
        blob: Blob;
        mimeType: string;
        durationSec: number;
        sourceName: string;
        thumbBlob: Blob | null;
        thumbUrl: string | null;
      }
    | null
  >(null);
  const [playbackVerified, setPlaybackVerified] = useState(false);
  const [publishStage, setPublishStage] = useState<PublishStage>("idle");
  const [publishError, setPublishError] = useState<{ stage: PublishStage; message: string } | null>(null);


  const generate = useMutation({
    mutationFn: () => gen({ data: { topic, title: title || undefined } }),
    onSuccess: (r) => {
      toast.success("Episode drafted", { description: r.ttsNote });
      setTopic("");
      setTitle("");
      qc.invalidateQueries({ queryKey: ["admin-radio"] });
    },
    onError: (e: any) =>
      toast.error("Generation failed", { description: String(e?.message ?? e) }),
  });

  async function handleUpload(file: File) {
    if (!file) return;
    setUploading(true);
    try {
      const path = `music/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_")}`;
      const { error } = await supabase.storage
        .from("radio-media")
        .upload(path, file, { contentType: file.type || "audio/mpeg" });
      if (error) throw error;
      await createTrack({
        data: {
          title: trackTitle || file.name.replace(/\.[^.]+$/, ""),
          artist: trackArtist,
          path,
        },
      });
      toast.success("Track uploaded");
      setTrackTitle("");
      setTrackArtist("");
      qc.invalidateQueries({ queryKey: ["admin-radio"] });
    } catch (e: any) {
      toast.error("Upload failed", { description: String(e?.message ?? e) });
    } finally {
      setUploading(false);
    }
  }

  async function handleVideoWatermark(file: File) {
    if (!file) return;
    if (!/^video\//.test(file.type)) {
      toast.error("Wrong file type", { description: `Expected a video file but got "${file.type || "unknown"}".` });
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error("Video too large", {
        description: `Max upload is ${(MAX_VIDEO_BYTES / 1024 / 1024).toFixed(0)} MB. This file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`,
      });
      return;
    }
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview.url);
      if (videoPreview.thumbUrl) URL.revokeObjectURL(videoPreview.thumbUrl);
      setVideoPreview(null);
    }
    setPlaybackVerified(false);
    setPublishError(null);
    setVideoBusy(true);
    setVideoProgress({ phase: "loading", ratio: 0 });
    try {
      const { blob, mimeType, durationSec, thumbBlob } = await watermarkVideo(file, setVideoProgress);
      if (!blob || blob.size === 0) {
        throw new Error("Watermarking produced an empty video. Try a different source file.");
      }
      const url = URL.createObjectURL(blob);
      const thumbUrl = thumbBlob ? URL.createObjectURL(thumbBlob) : null;
      setVideoPreview({
        url,
        blob,
        mimeType,
        durationSec,
        sourceName: file.name.replace(/\.[^.]+$/, ""),
        thumbBlob,
        thumbUrl,
      });
      toast.success("Watermark applied — preview and verify before publishing");
    } catch (e: any) {
      toast.error("Watermarking failed", { description: String(e?.message ?? e) });
    } finally {
      setVideoBusy(false);
      setVideoProgress(null);
    }
  }

  function discardVideoPreview() {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview.url);
      if (videoPreview.thumbUrl) URL.revokeObjectURL(videoPreview.thumbUrl);
    }
    setVideoPreview(null);
    setPlaybackVerified(false);
    setPublishError(null);
    setPublishStage("idle");
  }

  async function confirmVideoUpload() {
    if (!videoPreview) {
      setPublishError({ stage: "idle", message: "No watermarked video in the preview. Re-upload the source file." });
      return;
    }
    if (!playbackVerified) {
      setPublishError({ stage: "idle", message: "Tick 'Playback confirmed' after watching the verification clip." });
      return;
    }
    const titleVal = (videoTitle || videoPreview.sourceName).trim();
    if (titleVal.length < 2) {
      setPublishError({ stage: "idle", message: "Title must be at least 2 characters." });
      return;
    }
    const { blob, mimeType, durationSec, sourceName, thumbBlob } = videoPreview;
    if (!blob || blob.size === 0) {
      setPublishError({ stage: "idle", message: "Watermarked video is empty. Re-process the source file." });
      return;
    }
    setVideoBusy(true);
    setPublishError(null);

    const ext = mimeType.includes("webm") ? "webm" : "mp4";
    const safeBase = titleVal.replace(/[^a-zA-Z0-9.\-_]/g, "_") || "video";
    const stamp = Date.now();
    const videoPath = `videos/${stamp}-${safeBase}.${ext}`;
    let thumbPath: string | undefined;

    try {
      if (thumbBlob && thumbBlob.size > 0) {
        setPublishStage("uploading-thumb");
        thumbPath = `videos/${stamp}-${safeBase}.jpg`;
        const { error: thumbErr } = await supabase.storage
          .from("radio-media")
          .upload(thumbPath, thumbBlob, { contentType: "image/jpeg", upsert: false });
        if (thumbErr) {
          // Non-fatal — proceed without thumb.
          thumbPath = undefined;
          console.warn("Thumbnail upload skipped:", thumbErr.message);
        }
      }

      setPublishStage("uploading-video");
      const { error: vidErr } = await supabase.storage
        .from("radio-media")
        .upload(videoPath, blob, { contentType: mimeType, upsert: false });
      if (vidErr) {
        throw Object.assign(new Error(vidErr.message || "Storage upload failed"), { stage: "uploading-video" as PublishStage });
      }

      setPublishStage("recording-track");
      await createTrack({
        data: {
          title: titleVal || sourceName,
          artist: videoArtist,
          path: videoPath,
          mediaKind: "video",
          mimeType,
          durationSec: durationSec > 0 ? durationSec : undefined,
          thumbnailPath: thumbPath,
        },
      });

      setPublishStage("done");
      toast.success("Video published to /radio");
      setVideoTitle("");
      setVideoArtist("");
      discardVideoPreview();
      qc.invalidateQueries({ queryKey: ["admin-radio"] });
    } catch (e: any) {
      const stage: PublishStage = e?.stage ?? publishStage;
      const message =
        stage === "uploading-thumb"
          ? `Thumbnail upload failed: ${e?.message ?? e}`
          : stage === "uploading-video"
            ? `Video upload to storage failed: ${e?.message ?? e}`
            : stage === "recording-track"
              ? `Saved to storage, but recording the track failed: ${e?.message ?? e}. Retry to register it without re-uploading.`
              : `Publish failed: ${e?.message ?? e}`;
      setPublishError({ stage, message });
      toast.error("Publish failed", { description: message });
    } finally {
      setVideoBusy(false);
      setPublishStage((s) => (s === "done" ? "idle" : s));
    }
  }


  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-display text-2xl text-navy">Radio</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Generate talk sessions with AI and upload music tracks. Published
          episodes and active tracks stream automatically on{" "}
          <a className="underline" href="/radio">
            /radio
          </a>
          . Voice synthesis runs only when the{" "}
          <code className="rounded bg-muted px-1">ELEVENLABS_API_KEY</code>{" "}
          secret is set — otherwise episodes save as text-only scripts.
        </p>
      </div>

      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg text-navy">
          <Sparkles className="mr-2 inline h-4 w-4 text-gold" />
          Generate a new talk session
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Today's pressing topic</Label>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={3}
              placeholder="e.g. Unfair dismissal at retail jobs — how to take your employer to the CCMA without a lawyer"
            />
          </div>
          <div>
            <Label>Optional title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
        </div>
        <Button
          className="mt-4 bg-navy hover:bg-navy/90"
          disabled={generate.isPending || topic.trim().length < 4}
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…
            </>
          ) : (
            <>
              <Mic2 className="mr-2 h-4 w-4" /> Generate episode
            </>
          )}
        </Button>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg text-navy">
          <Music2 className="mr-2 inline h-4 w-4" /> Upload music track
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div>
            <Label>Title</Label>
            <Input
              value={trackTitle}
              onChange={(e) => setTrackTitle(e.target.value)}
              placeholder="Track title"
            />
          </div>
          <div>
            <Label>Artist</Label>
            <Input
              value={trackArtist}
              onChange={(e) => setTrackArtist(e.target.value)}
              placeholder="Artist"
            />
          </div>
          <div>
            <Label>Audio file (mp3, m4a, wav)</Label>
            <Input
              type="file"
              accept="audio/*"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = "";
              }}
            />
          </div>
        </div>
        {uploading && (
          <p className="mt-3 inline-flex items-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg text-navy">
          <Video className="mr-2 inline h-4 w-4" /> Upload video (auto-branded with logo)
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The video is watermarked in your browser with the NDL logo and copyright
          strap before upload — the published file carries the brand burned in.
          Processing happens locally and runs at roughly real-time length.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div>
            <Label>Title</Label>
            <Input
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              placeholder="Video title"
              disabled={videoBusy}
            />
          </div>
          <div>
            <Label>Presenter / source</Label>
            <Input
              value={videoArtist}
              onChange={(e) => setVideoArtist(e.target.value)}
              placeholder="e.g. NDL Studio"
              disabled={videoBusy}
            />
          </div>
          <div>
            <Label>Video file (mp4, mov, webm)</Label>
            <Input
              type="file"
              accept="video/*"
              disabled={videoBusy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleVideoWatermark(f);
                e.target.value = "";
              }}
            />
          </div>
        </div>
        {videoBusy && publishStage === "idle" && (
          <div className="mt-3 space-y-2">
            <p className="inline-flex items-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {videoProgress?.phase === "loading"
                ? "Reading video…"
                : videoProgress?.phase === "encoding"
                  ? "Burning in logo & copyright…"
                  : "Working…"}
            </p>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-navy transition-all"
                style={{ width: `${Math.round((videoProgress?.ratio ?? 0) * 100)}%` }}
              />
            </div>
          </div>
        )}
        {videoBusy && publishStage !== "idle" && (
          <p className="mt-3 inline-flex items-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {publishStage === "uploading-thumb"
              ? "Uploading thumbnail…"
              : publishStage === "uploading-video"
                ? "Uploading watermarked video…"
                : publishStage === "recording-track"
                  ? "Registering track in the library…"
                  : "Finishing…"}
          </p>
        )}
        {publishError && !videoBusy && (
          <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            <p className="font-medium">Publish failed at: {publishError.stage.replace(/-/g, " ") || "validation"}</p>
            <p className="mt-1">{publishError.message}</p>
            <p className="mt-1 text-xs text-red-600/80">
              Fix the issue and click Publish again — the watermarked video stays in this preview so you don't have to re-process it.
            </p>
          </div>
        )}
        {videoPreview && !videoBusy && (
          <div className="mt-4 space-y-4 rounded-xl border border-border bg-muted/40 p-4">
            <div>
              <p className="text-sm font-medium text-navy">
                1 · Preview — watermarked video (not yet published)
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Confirm the NDL logo, copyright strap, audio, and that the content looks correct.
              </p>
              <video
                controls
                playsInline
                preload="metadata"
                poster={videoPreview.thumbUrl ?? undefined}
                className="mt-3 aspect-video w-full max-w-2xl rounded-md bg-black"
                src={videoPreview.url}
              />
            </div>

            <div className="border-t border-border/60 pt-4">
              <p className="text-sm font-medium text-navy">
                2 · Second playback check — verify the burned video plays end-to-end
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                This is a separate player loading the exact bytes that will be uploaded. Watch it through, then tick the box.
              </p>
              <video
                controls
                playsInline
                preload="metadata"
                poster={videoPreview.thumbUrl ?? undefined}
                className="mt-3 aspect-video w-full max-w-2xl rounded-md bg-black"
                src={videoPreview.url}
                onEnded={() => setPlaybackVerified(true)}
              />
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-navy">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={playbackVerified}
                  onChange={(e) => setPlaybackVerified(e.target.checked)}
                />
                Playback confirmed — the watermarked video plays correctly.
              </label>
            </div>

            {videoPreview.thumbUrl && (
              <div className="border-t border-border/60 pt-4">
                <p className="text-sm font-medium text-navy">3 · Auto-generated thumbnail</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This poster image will appear in the /radio Video gallery.
                </p>
                <img
                  src={videoPreview.thumbUrl}
                  alt="Video thumbnail preview"
                  className="mt-3 aspect-video w-full max-w-xs rounded-md border border-border bg-black object-cover"
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-border/60 pt-4">
              <Button
                className="bg-navy hover:bg-navy/90"
                onClick={confirmVideoUpload}
                disabled={videoBusy || !playbackVerified}
                title={!playbackVerified ? "Tick 'Playback confirmed' first" : undefined}
              >
                <Upload className="mr-2 h-4 w-4" /> Publish to /radio
              </Button>
              <Button
                variant="outline"
                onClick={discardVideoPreview}
                disabled={videoBusy}
              >
                Discard
              </Button>
              <a
                href={videoPreview.url}
                download={`${(videoTitle || videoPreview.sourceName).replace(/[^a-zA-Z0-9.\-_]/g, "_")}.${videoPreview.mimeType.includes("webm") ? "webm" : "mp4"}`}
                className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
              >
                Download local copy
              </a>
            </div>
          </div>
        )}

      </section>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading…
        </p>
      ) : (
        <>
          <section>
            <h2 className="font-display text-lg text-navy">Episodes</h2>
            <ul className="mt-3 divide-y divide-border rounded-xl border border-border bg-card">
              {(data?.episodes ?? []).map((e: any) => (
                <li key={e.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-navy">{e.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {e.status.toUpperCase()} · {e.topic}
                      </p>
                      {e.audio_signed_url ? (
                        <audio
                          controls
                          preload="none"
                          className="mt-2 w-full max-w-md"
                          src={e.audio_signed_url}
                        />
                      ) : (
                        <p className="mt-1 text-xs text-amber-700">
                          Text-only (no voice file yet)
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      {e.status !== "published" && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            await setStatus({
                              data: { id: e.id, status: "published" },
                            });
                            qc.invalidateQueries({ queryKey: ["admin-radio"] });
                          }}
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Publish
                        </Button>
                      )}
                      {e.status === "published" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await setStatus({
                              data: { id: e.id, status: "archived" },
                            });
                            qc.invalidateQueries({ queryKey: ["admin-radio"] });
                          }}
                        >
                          Archive
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={async () => {
                          if (!confirm("Delete this episode?")) return;
                          await delEp({ data: { id: e.id } });
                          qc.invalidateQueries({ queryKey: ["admin-radio"] });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <details className="mt-3 text-sm">
                    <summary className="cursor-pointer text-muted-foreground">
                      Script
                    </summary>
                    <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                      {e.script}
                    </pre>
                  </details>
                </li>
              ))}
              {(data?.episodes ?? []).length === 0 && (
                <li className="p-6 text-center text-sm text-muted-foreground">
                  No episodes yet.
                </li>
              )}
            </ul>
          </section>

          <section>
            <h2 className="font-display text-lg text-navy">Music tracks</h2>
            <ul className="mt-3 divide-y divide-border rounded-xl border border-border bg-card">
              {(data?.tracks ?? []).map((t: any) => (
                <li
                  key={t.id}
                  className="flex flex-wrap items-center justify-between gap-3 p-4"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-navy">
                      {t.media_kind === "video" ? <Video className="mr-1 inline h-3.5 w-3.5" /> : <Music2 className="mr-1 inline h-3.5 w-3.5" />}
                      {t.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.artist || "—"} · {t.media_kind === "video" ? "video" : "audio"} · {t.active ? "active" : "hidden"}
                    </p>
                    {t.audio_signed_url && (
                      t.media_kind === "video" ? (
                        <video
                          controls
                          preload="metadata"
                          className="mt-2 w-full max-w-md rounded-md bg-black"
                          src={t.audio_signed_url}
                        />
                      ) : (
                        <audio
                          controls
                          preload="none"
                          className="mt-2 w-full max-w-md"
                          src={t.audio_signed_url}
                        />
                      )
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await toggleTrack({
                          data: { id: t.id, active: !t.active },
                        });
                        qc.invalidateQueries({ queryKey: ["admin-radio"] });
                      }}
                    >
                      {t.active ? (
                        <>
                          <Pause className="mr-1 h-4 w-4" /> Hide
                        </>
                      ) : (
                        <>
                          <Play className="mr-1 h-4 w-4" /> Activate
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (!confirm("Delete this track?")) return;
                        await delTrack({ data: { id: t.id } });
                        qc.invalidateQueries({ queryKey: ["admin-radio"] });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
              {(data?.tracks ?? []).length === 0 && (
                <li className="p-6 text-center text-sm text-muted-foreground">
                  No tracks yet. <Upload className="inline h-3.5 w-3.5" />{" "}
                  Upload one above.
                </li>
              )}
            </ul>
          </section>

          <RadioTextToVideo />
        </>
      )}
    </div>
  );
}

async function watermarkVideo(
  file: File,
  onProgress?: (p: WatermarkProgress) => void,
): Promise<{ blob: Blob; mimeType: string; durationSec: number; thumbBlob: Blob | null }> {

  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("Video watermarking is only available in the browser.");
  }
  if (typeof MediaRecorder === "undefined") {
    throw new Error("Your browser does not support video recording. Use Chrome or Firefox.");
  }

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.crossOrigin = "anonymous";
  const url = URL.createObjectURL(file);
  video.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Could not read video file."));
    });
    onProgress?.({ phase: "loading", ratio: 0.1 });

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not available.");

    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.src = logoAsset.url;
    await new Promise<void>((resolve) => {
      logo.onload = () => resolve();
      logo.onerror = () => resolve();
    });

    const canvasStream = canvas.captureStream(30);
    let audioTrack: MediaStreamTrack | null = null;
    try {
      const captured = (video as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
      const audio = captured?.getAudioTracks?.()[0];
      if (audio) {
        audioTrack = audio;
        canvasStream.addTrack(audio);
      }
    } catch {
      // Continue with a silent branded video when browser audio capture is unavailable.
    }

    const mimeType =
      ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"].find((m) =>
        MediaRecorder.isTypeSupported(m),
      ) ?? "video/webm";
    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: 2_500_000 });
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };

    const drawFrame = () => {
      ctx.drawImage(video, 0, 0, width, height);
      const logoHeight = Math.max(48, Math.round(height * 0.09));
      const logoAspect = logo.naturalWidth && logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : 1;
      const logoWidth = Math.round(logoHeight * logoAspect);
      const pad = Math.round(height * 0.025);
      if (logo.naturalWidth) {
        ctx.globalAlpha = 0.92;
        ctx.drawImage(logo, width - logoWidth - pad, pad, logoWidth, logoHeight);
        ctx.globalAlpha = 1;
      }

      const stripHeight = Math.max(36, Math.round(height * 0.07));
      const grad = ctx.createLinearGradient(0, height - stripHeight, 0, height);
      grad.addColorStop(0, "rgba(10,22,53,0)");
      grad.addColorStop(1, "rgba(10,22,53,0.78)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, height - stripHeight, width, stripHeight);

      ctx.fillStyle = "#ffffff";
      ctx.font = `600 ${Math.round(stripHeight * 0.42)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textBaseline = "middle";
      ctx.fillText(BRAND_LINE, pad, height - stripHeight / 2);

      ctx.fillStyle = "#d4af37";
      ctx.font = `500 ${Math.round(stripHeight * 0.34)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      const tag = "NDL Justice Radio";
      ctx.fillText(tag, width - ctx.measureText(tag).width - pad, height - stripHeight / 2);
    };

    let raf = 0;
    const tick = () => {
      if (video.paused || video.ended) return;
      drawFrame();
      if (duration > 0) {
        onProgress?.({ phase: "encoding", ratio: Math.min(0.95, 0.1 + 0.85 * (video.currentTime / duration)) });
      }
      raf = requestAnimationFrame(tick);
    };

    recorder.start(1000);
    await video.play();
    tick();

    // Capture a thumbnail of the first watermarked frame for the /radio gallery poster.
    drawFrame();
    const thumbBlob = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.82);
      } catch {
        resolve(null);
      }
    });

    await new Promise<void>((resolve) => {
      video.onended = () => resolve();
    });
    cancelAnimationFrame(raf);
    drawFrame();
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    canvasStream.getTracks().forEach((track) => track.stop());
    audioTrack?.stop();
    onProgress?.({ phase: "done", ratio: 1 });
    return { blob: new Blob(chunks, { type: mimeType }), mimeType, durationSec: Math.round(duration), thumbBlob };

  } finally {
    URL.revokeObjectURL(url);
  }
}
